import classNames from 'classnames';
import React, { useState } from 'react';
import Badge from '../../components/Badge';
import Button, { IconButton } from '../../components/Button';
import CallOut from '../../components/CallOut';
import Card from '../../components/Card';
import { MenuItem } from '../../components/Menu';
import OverflowMenu from '../../components/OverflowMenu';
import SVGIcon from '../../components/SVGIcon';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import alertIcon from '../../icons/non-prefilled/16x16/alert.svg';
import overflowMenuIcon from '../../icons/non-prefilled/16x16/overflowMenu.svg';
import * as styles from './CustomDomain.module.scss';

const CUSTOM_DOMAIN_FRAGMENT = gql(`
  fragment CustomDomain_customDomain on CustomDomain {
    active
    hostname
  }
`);

interface CustomDomainProps {
  customDomain: DocumentType<typeof CUSTOM_DOMAIN_FRAGMENT>;
  isDefault: boolean;
  onChangeDefault: (isDefault: boolean) => void;
  onRemove: () => void;
  onRefresh: () => void;
}

const CustomDomain = ({
  customDomain,
  isDefault,
  onChangeDefault,
  onRemove,
  onRefresh,
}: CustomDomainProps) => {
  const { hostname, active: isActive } = customDomain;
  const [isActionsOpen, setIsActionsOpen] = useState(false);

  return (
    <Card noPadding>
      <div className={styles.domainRow}>
        {!isActive && (
          <SVGIcon className={styles.errorIcon}>{alertIcon}</SVGIcon>
        )}
        <div className={styles.domainAndBadges}>
          {hostname}
          {isDefault && (
            <span className={styles.badgeContainer}>
              <Badge color="yellow">Default</Badge>
            </span>
          )}
          {isActive && (
            <span className={styles.badgeContainer}>
              <Badge color="green">Live</Badge>
            </span>
          )}
        </div>
        <div className={styles.actions}>
          <OverflowMenu
            button={
              <IconButton
                className={classNames(styles.overflowButton, {
                  [styles.active]: isActionsOpen,
                })}
                aria-label="Options"
                size="xl"
              >
                {overflowMenuIcon}
              </IconButton>
            }
            open={isActionsOpen}
            onOpenChange={setIsActionsOpen}
          >
            {isActive &&
              (isDefault ? (
                <MenuItem onClick={() => onChangeDefault(false)}>
                  Remove as default
                </MenuItem>
              ) : (
                <MenuItem onClick={() => onChangeDefault(true)}>
                  Set as default
                </MenuItem>
              ))}
            <MenuItem danger onClick={onRemove}>
              Remove domain
            </MenuItem>
          </OverflowMenu>
        </div>
      </div>
      {!isActive && (
        <div className={styles.errorCallOutContainer}>
          <CallOut variant="danger">
            <div className={styles.errorCallOut}>
              <div className={styles.errorInfo}>
                <div className={styles.errorTitle}>DNS update required</div>
                Add a <code className={styles.errorCode}>CNAME</code> record for{' '}
                {hostname} pointing to{' '}
                <code className={styles.errorCode}>
                  app.
                  {window.__slab.HOST}
                </code>
              </div>
              <Button
                className={styles.errorAction}
                onClick={onRefresh}
                variant="secondary"
              >
                Refresh
              </Button>
            </div>
          </CallOut>
        </div>
      )}
    </Card>
  );
};

export default CustomDomain;
